import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { cleanTags, formatNumber } from './dataDictionaryUtils';

/**
 * Chip input for tags with typeahead over existing tags (with usage counts).
 * `options` = [{tag, count}]. `value` = string[]. New tags are created inline when `allowCreate`.
 */
export default function TagPicker({
  value,
  onChange,
  options = [],
  placeholder = 'Add tag…',
  allowCreate = true,
  disabled = false,
  autoFocus = false,
  ariaLabel = 'Tags',
}) {
  const id = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const tags = useMemo(() => cleanTags(value), [value]);
  const lowerSet = useMemo(() => new Set(tags.map((t) => t.toLowerCase())), [tags]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const needle = query.trim().toLowerCase();
  const suggestions = useMemo(() => {
    const list = options.filter((o) => o?.tag && (!needle || o.tag.toLowerCase().includes(needle)));
    list.sort((a, b) => {
      const as = a.tag.toLowerCase().startsWith(needle) ? 0 : 1;
      const bs = b.tag.toLowerCase().startsWith(needle) ? 0 : 1;
      return as - bs || (b.count || 0) - (a.count || 0) || a.tag.localeCompare(b.tag);
    });
    return list.slice(0, 12);
  }, [options, needle]);

  const exactExists = needle && (lowerSet.has(needle) || options.some((o) => o.tag.toLowerCase() === needle));
  const canCreate = allowCreate && needle && !exactExists;
  const items = useMemo(() => {
    const out = suggestions.map((s) => ({ kind: 'tag', tag: s.tag, count: s.count }));
    if (canCreate) out.push({ kind: 'create', tag: query.trim() });
    return out;
  }, [suggestions, canCreate, query]);

  useEffect(() => {
    setActive(0);
  }, [needle]);

  const commit = (tag) => {
    const t = String(tag || '').trim();
    if (!t) return;
    if (lowerSet.has(t.toLowerCase())) {
      onChange(tags.filter((x) => x.toLowerCase() !== t.toLowerCase()));
    } else {
      onChange([...tags, t]);
    }
    setQuery('');
    inputRef.current?.focus();
  };

  const remove = (tag) => onChange(tags.filter((x) => x !== tag));

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ',') {
      if (!query.trim() && e.key === 'Enter') return;
      e.preventDefault();
      const item = open && items[active];
      if (item) commit(item.tag);
      else if (allowCreate && query.trim()) commit(query);
    } else if (e.key === 'Backspace' && !query && tags.length) {
      e.preventDefault();
      remove(tags[tags.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div
        className={`flex min-h-[38px] flex-wrap items-center gap-1 rounded-lg border bg-surface px-2 py-1.5 text-sm transition-colors ${
          open ? 'border-[#18181B]/40' : 'border-hairline'
        } ${disabled ? 'opacity-60' : ''}`}
        onClick={() => !disabled && inputRef.current?.focus()}
        role="presentation"
      >
        {tags.map((tag) => (
          <span key={tag} className="inline-flex h-6 items-center gap-1 rounded-md bg-[#F3F2EE] px-2 text-xs font-medium text-[#3F3F46]">
            {tag}
            {!disabled ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(tag);
                }}
                className="rounded text-zinc-400 hover:text-zinc-700"
                aria-label={`Remove tag ${tag}`}
              >
                <X size={11} aria-hidden />
              </button>
            ) : null}
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={tags.length ? '' : placeholder}
          aria-label={ariaLabel}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          className="min-w-[6rem] flex-1 bg-transparent py-0.5 text-sm placeholder:text-zinc-400 focus:outline-none"
        />
      </div>

      {open && !disabled && (items.length > 0 || needle) ? (
        <div
          id={`${id}-list`}
          role="listbox"
          className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-auto rounded-xl border border-hairline bg-white p-1 shadow-[0_12px_32px_rgba(24,24,27,0.12),0_2px_6px_rgba(24,24,27,0.06)]"
        >
          {items.length === 0 ? (
            <p className="px-3 py-2 text-xs text-zinc-500">No matching tags.</p>
          ) : (
            items.map((item, i) => {
              const selected = item.kind === 'tag' && lowerSet.has(item.tag.toLowerCase());
              return (
                <button
                  key={`${item.kind}:${item.tag}`}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(item.tag)}
                  className={`flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left text-[13px] ${
                    i === active ? 'bg-primary-50' : ''
                  } ${item.kind === 'create' ? 'text-primary-700' : 'text-[#18181B]'}`}
                >
                  {item.kind === 'create' ? (
                    <>
                      <Plus size={14} aria-hidden />
                      <span>
                        Create tag <span className="rounded bg-primary-50 px-1.5 py-0.5 font-medium">{item.tag}</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        className={`inline-flex h-4 w-4 items-center justify-center rounded border ${
                          selected ? 'border-[#0062FB] bg-[#0062FB] text-white' : 'border-zinc-300'
                        }`}
                        aria-hidden
                      >
                        {selected ? <Check size={11} strokeWidth={3} /> : null}
                      </span>
                      <span className="flex-1 truncate">{item.tag}</span>
                      <span className="text-xs tabular-nums text-zinc-400">{formatNumber(item.count)}</span>
                    </>
                  )}
                </button>
              );
            })
          )}
          <div className="flex gap-3 border-t border-hairline px-2.5 pb-1 pt-2 text-[11px] text-zinc-400">
            <span><kbd className="rounded border border-hairline px-1">↵</kbd> add</span>
            <span><kbd className="rounded border border-hairline px-1">⌫</kbd> remove last</span>
            <span><kbd className="rounded border border-hairline px-1">,</kbd> separator</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
